import { connect, FormikValues } from 'formik'
import { flatten } from 'lodash'
import { useCallback, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'
import { string } from 'yup'

import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import Label from '@/components/Label'
import { Row } from '@/components/StyledComponents'
import {
  SecondaryRoundedButton,
  StyledWandIcon,
  VariableInputContainer
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import { FieldTypes, LogStepData, StepData, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import messages from './messages'

//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type OutputProps = {
  readOnly?: boolean
}
//</editor-fold>

const getValidationSchema = (intl: IntlShape) => {
  return {
    message: string().trim().required(intl.formatMessage(messages.logMessageRequired))
  }
}

const getInitialValues = (stepData: LogStepData): StepData => ({ message: stepData.message })
const getLogOperationHelpUrl = 'https://help.workgrid.com/s/article/Log'
const LogOperation = ({ formik, readOnly = false }: OutputProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()
  const flattenedVariables = flatten(variables)
  const addVariableRef = useRef<any>(() => {})
  const [showVariablePopOver, setShowVariablePopOver] = useState(false)

  // @ts-expect-error
  const addMention = useCallback(addMentionFn => (addVariableRef.current = addMentionFn), [])

  return (
    <div data-testid="log-property-panel">
      <StyledRow>
        <Label required>{intl.formatMessage(messages.logMessage)}</Label>
      </StyledRow>
      <VariableInputContainer>
        <MentionsRichTextEditorWithFormik
          readOnly={readOnly}
          setAddMention={addMention}
          name={'stepData.message'}
          formik={formik}
          mentions={getAllFlattenedVariables(flattenedVariables)}
          placeholder={intl.formatMessage(messages.addMessage)}
        />
        {flattenedVariables?.length > 0 && !readOnly && (
          <>
            {showVariablePopOver && (
              <VariableOverlay
                onSelectVariable={(variable: Variable) => addVariableRef.current(variable)}
                onClose={() => setShowVariablePopOver(false)}
                variables={variables}
                validVariableTypes={[
                  FieldTypes.STRING,
                  FieldTypes.BOOLEAN,
                  FieldTypes.INTEGER,
                  FieldTypes.NUMBER,
                  FieldTypes.ANY
                ]}
              />
            )}
            <SecondaryRoundedButton
              onClick={() => {
                setShowVariablePopOver(true)
              }}
            >
              <>
                <StyledWandIcon />
                <span>{intl.formatMessage(messages.addVariableButton)}</span>
              </>
            </SecondaryRoundedButton>
          </>
        )}
      </VariableInputContainer>
    </div>
  )
}

export default connect(LogOperation)

export { getInitialValues, getValidationSchema, getLogOperationHelpUrl }
